// @flow

import React, { createRef } from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { localize } from "redux-i18n";

import * as serviceSelectors from "@modules/service/selectors";
import { getBradfordScore } from "@modules/settings/selectors";
import actionCreator from "@modules/service/actionCreator";
import { Col, Row, Button } from "@components";
import type { StepProps } from "@components";
import { AlertTypeForm } from "@modules/service/components";
import type { Alert } from "@modules/service/reducer/alerts/types";

type StepData = $Exact<{
  alertType: string,
}>;

type OwnProps = $Exact<{
  editing: boolean,
}>;

type PropsFromState = $Exact<{
  schema: ?Object,
  draft: ?Alert,
  bradfordScore: boolean,
}>;

type PropsFromDispatch = $Exact<{
  setDraft: typeof actionCreator.SetAlertDraft,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...StepProps,
  ...PropsFromLocalize,
}>;

export class Step0AlertType extends React.Component<Props> {
  formRef: * = createRef();

  get data(): StepData {
    return { alertType: ((this.props.draft || {}).content?.details || {}).alertType };
  }

  get schema() {
    const details = (this.props.schema || {}).properties.details;

    if (!this.props.bradfordScore && details?.properties?.alertType?.oneOf) {
      return {
        details: {
          ...details,
          properties: {
            ...details.properties,
            alertType: {
              ...details.properties.alertType,
              oneOf: details.properties.alertType.oneOf.filter(
                option => option.const !== "BRADFORD_SCORE_OVER_X_IN_Y_MONTHS",
              ),
            },
          },
        },
      };
    }

    return { details };
  }

  submitForm = () => {
    if (this.formRef) {
      this.formRef.current.submit();
    }
  };

  submitDetails = (formData: Object) => {
    if (this.props.handleSubmit) {
      this.props.setDraft({
        ...this.props.draft,
        content: {
          ...this.props.draft?.content,
          details: {
            name: ((this.props.draft || {}).content.details || {}).name,
            isActive: ((this.props.draft || {}).content.details || {}).isActive,
            absenceType: ((this.props.draft || {}).content.details || {}).absenceType,
            alertPurpose: ((this.props.draft || {}).content.details || {}).alertPurpose,
            triggerMode: ((this.props.draft || {}).content.details || {}).triggerMode,
            activeFrom: ((this.props.draft || {}).content.details || {}).activeFrom,
            activeTo: ((this.props.draft || {}).content.details || {}).activeTo,
            notifyEmployeeContacts:
              ((this.props.draft || {}).content.details || {}).notifyEmployeeContacts || false,
            ...formData,
          },
        },
      });
      this.props.handleSubmit({}, true);
    }
  };

  render() {
    if (!this.props.editable) {
      return <div />;
    }

    return (
      <React.Fragment>
        <Row className="mt-4 w-100 d-flex justify-content-center">
          <Col sm="8">
            {this.schema && this.data ? (
              <AlertTypeForm
                formRef={this.formRef}
                data={this.data}
                schema={this.schema.details}
                onSubmit={this.submitDetails}
              />
            ) : null}
          </Col>
        </Row>
        {this.props.editing ? null : (
          <div className="w-100 d-flex justify-content-end mt-5 mb-3">
            <Button materialIcon="fast_forward" onClick={this.submitForm}>
              {this.props.t("NEXT")}
            </Button>
          </div>
        )}
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state): PropsFromState => {
  return {
    schema: serviceSelectors.getAlertSchema(state),
    draft: serviceSelectors.getAlertDraft(state),
    bradfordScore: getBradfordScore(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  setDraft: actionCreator.SetAlertDraft,
};

export default compose(localize(), connect(mapStateToProps, mapDispatchToProps))(Step0AlertType);
